import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useMemo,
} from "react";
import { useStoryboardWsStore } from "@/store/storyboardWsStore";
import { useStreamingAudio } from "@/hooks/useStreamingAudio";

export interface TTSPlayOptions {
  sectionId?: string;
  slideId?: string;
  text?: string;
  /** Client-only cache/playback key, never sent over the wire. Overrides
   * slideId/sectionId for local dedup when neither is a real backend id. */
  cacheKey?: string;
  /** Overrides the provider's default voiceId for this call's cache check
   * and request, e.g. a resolved per-section voice. */
  voiceId?: string;
}

export interface TTSContextValue {
  play: (options: TTSPlayOptions) => void;
  pause: () => Promise<void>;
  resume: (fallback?: TTSPlayOptions) => Promise<void>;
  stop: () => void;
  currentlyPlayingId: string | null;
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  isConnected: boolean;
  hasError: boolean;
  wsBusy: boolean;
}

export const TTSContext = createContext<TTSContextValue | null>(null);

interface TTSProviderProps {
  children: ReactNode;
  /**
   * Default identifier for the current voice/presenter, used when a play()
   * call doesn't resolve its own (e.g. no section-specific override). Included
   * in the TTS cache key so that changing presenter naturally invalidates
   * previously cached audio for the same text.
   */
  voiceId?: string;
}

/**
 * TTSProvider - Provides centralized text-to-speech playback management.
 *
 * This provider coordinates TTS playback across multiple PlayScript components,
 * ensuring only one audio stream plays at a time. When a new playback is requested,
 * any currently playing audio is automatically stopped.
 *
 * @example
 * <TTSProvider>
 *   <StoryboardContent>
 *     <PlayScript sectionId={section.id} text={script} />
 *   </StoryboardContent>
 * </TTSProvider>
 */
export const TTSProvider = ({
  children,
  voiceId: defaultVoiceId,
}: TTSProviderProps) => {
  const {
    requestTextToSpeech,
    subscribeToTTS,
    isConnected,
    addMessageHandler,
    getTTSCache,
    isBusy: wsBusy,
  } = useStoryboardWsStore();

  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(
    null
  );

  const handlePlaybackComplete = useCallback(() => {
    setCurrentlyPlayingId(null);
  }, []);

  const { state, addChunk, pause, resume, stop, reset, prepare } =
    useStreamingAudio(handlePlaybackComplete);
  const [isWaiting, setIsWaiting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const unsubscribeMessageRef = useRef<(() => void) | null>(null);
  const queryIdRef = useRef<string | null>(null);

  const stopPlayback = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (unsubscribeMessageRef.current) {
      unsubscribeMessageRef.current();
      unsubscribeMessageRef.current = null;
    }
    queryIdRef.current = null;
    setCurrentlyPlayingId(null);
    setIsWaiting(false);
    setHasError(false);
    stop();
  }, [stop]);

  const play = useCallback(
    ({ sectionId, slideId, text, cacheKey, voiceId }: TTSPlayOptions) => {
      // Stop any currently playing audio before starting new playback
      stopPlayback();
      reset();

      const effectiveVoiceId = voiceId ?? defaultVoiceId;

      // Track by cacheKey if provided, then slideId, otherwise sectionId
      const playbackId = cacheKey ?? slideId ?? sectionId ?? null;
      setCurrentlyPlayingId(playbackId);
      setHasError(false);

      // Prepare AudioContext (Safari)
      prepare().catch((error) => {
        console.error("Error preparing audio context", error);
      });

      // Check cache first
      if (playbackId && text) {
        const cachedEntry = getTTSCache(playbackId, text, effectiveVoiceId);
        if (cachedEntry?.isComplete) {
          // Play from cache - feed all chunks to audio processor
          console.debug("[TTSProvider] Playing from cache:", playbackId);
          setIsWaiting(false);
          cachedEntry.chunks.forEach((chunk) => {
            addChunk(chunk).catch((error) => {
              console.error("Error adding chunk", error);
            });
          });
          return;
        }
      }

      // No cache or incomplete - request from server
      setIsWaiting(true);

      // Request TTS and get query_id
      const queryId = requestTextToSpeech({
        sectionId,
        slideId,
        text,
        voiceId: effectiveVoiceId,
        cacheKey,
      });

      // Handle connection failure
      if (!queryId) {
        setIsWaiting(false);
        setHasError(true);
        setCurrentlyPlayingId(null);
        return;
      }

      queryIdRef.current = queryId;

      // Subscribe to chunks for this query
      unsubscribeRef.current = subscribeToTTS(queryId, (chunk) => {
        // First chunk received - no longer waiting
        setIsWaiting(false);
        addChunk(chunk).catch((error) => {
          console.error("Error adding chunk", error);
        });
      });

      // Subscribe to message handler to detect errors
      unsubscribeMessageRef.current = addMessageHandler((message) => {
        if (message.query_id !== queryId) return;

        // Check for failure
        if (message.is_final && message.status !== "success") {
          setIsWaiting(false);
          setHasError(true);
          setCurrentlyPlayingId(null);
          reset();
          queryIdRef.current = null;
        }
      });
    },
    [
      stopPlayback,
      reset,
      prepare,
      requestTextToSpeech,
      subscribeToTTS,
      addChunk,
      addMessageHandler,
      getTTSCache,
      defaultVoiceId,
    ]
  );

  const handlePause = useCallback(async () => {
    await pause();
  }, [pause]);

  const handleResume = useCallback(
    async (fallback?: TTSPlayOptions) => {
      // If the voice has changed since playback started, the buffered audio is
      // stale. getTTSCache returns null when voiceId or text no longer match,
      // so we replay from scratch with the new voice in a single click.
      if (fallback?.text && currentlyPlayingId) {
        const entry = getTTSCache(
          currentlyPlayingId,
          fallback.text,
          fallback.voiceId ?? defaultVoiceId
        );
        if (!entry) {
          play(fallback);
          return;
        }
      }
      await resume();
    },
    [resume, currentlyPlayingId, getTTSCache, defaultVoiceId, play]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (unsubscribeMessageRef.current) {
        unsubscribeMessageRef.current();
      }
      stop();
    };
  }, [stop]);

  const value = useMemo<TTSContextValue>(
    () => ({
      play,
      pause: handlePause,
      resume: handleResume,
      stop: stopPlayback,
      currentlyPlayingId,
      isPlaying: state.isPlaying,
      isPaused: state.isPaused,
      isLoading: isWaiting || state.isLoading,
      isConnected,
      hasError,
      wsBusy,
    }),
    [
      play,
      handlePause,
      handleResume,
      stopPlayback,
      state,
      isWaiting,
      isConnected,
      hasError,
      currentlyPlayingId,
      wsBusy,
    ]
  );

  return <TTSContext.Provider value={value}>{children}</TTSContext.Provider>;
};

/**
 * Hook to access the raw TTS context value.
 * For most use cases, prefer useTextToSpeech(sectionId) which provides scoped state.
 *
 * @returns The TTS context value
 * @throws Error if used outside of TTSProvider
 */
export const useTTS = (): TTSContextValue => {
  const context = useContext(TTSContext);
  if (!context) {
    throw new Error("useTTS must be used within a TTSProvider");
  }
  return context;
};
